import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import moment from 'moment';
import 'moment/locale/en-gb';

import {
  FiCalendar,
  FiCheckCircle,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiMessageCircle,
  FiPhone,
  FiRefreshCw,
  FiTrash2,
  FiUploadCloud,
  FiUser,
  FiZap,
} from 'react-icons/fi';

import api from '../services/api';
import { useAuth } from '../auth/AuthContext';

import AppointmentImageViewer from './AppointmentImageViewer';

import './ArtistDashboard.css';
import ArtistMyWork, { ArtistProjectJourney } from './ArtistMyWork';

moment.locale('en-gb');

const STATUS_LABELS = {
  waiting: 'Waiting',
  arrived: 'Client here',
  in_progress: 'In progress',
  done: 'Done',
};

const STATUS_ORDER = [
  'waiting',
  'arrived',
  'in_progress',
  'done',
];

function formatTime(time) {
  if (!time) return '--:--';

  return moment(
    time,
    ['HH:mm:ss', 'HH:mm']
  ).format('HH:mm');
}

function cleanPhone(phone) {
  if (!phone) return '';

  return String(phone)
    .replace(/\D/g, '')
    .replace(/^0/, '49');
}

function appointmentType(appointment) {
  const text = String(
    appointment?.description || ''
  ).toLowerCase();

  if (
    text.includes('nachstechen') ||
    text.includes('touchup') ||
    text.includes('touch-up')
  ) {
    return 'Touch-up';
  }

  return 'Tattoo';
}

function getAppointmentImages(appointment) {
  if (!appointment) return [];

  const projectImages = Array.isArray(
    appointment.images
  )
    ? appointment.images.map((image) => ({
      id: `project-${image.id}`,
      fullUrl: image.url,
      thumbnailUrl:
        image.thumbnailUrl || image.url,
      isProjectImage: true,
    }))
    : [];

  const legacyImages =
    appointment.consultation_image_url
      ? appointment.consultation_image_url
        .split(',')
        .map((url) => url.trim())
        .filter(Boolean)
        .map((url, index) => ({
          id: `legacy-${index}-${url}`,
          fullUrl: url,
          thumbnailUrl: url,
          isProjectImage: false,
        }))
      : [];

  return [
    ...projectImages,
    ...legacyImages,
  ];
}

function ArtworkUploadSection({
  kind,
  title,
  hint,
  images,
  loading,
  uploading,
  deletingImageId,
  onUpload,
  onDelete,
  onOpen,
}) {
  const inputId =
    `artist-${kind}-upload`;

  return (
    <div className="artist-artwork-block">
      <div className="artist-artwork-heading">
        <div>
          <span>{title}</span>
          <small>{hint}</small>
        </div>

        <label
          htmlFor={inputId}
          className={
            uploading
              ? 'artist-upload-button is-loading'
              : 'artist-upload-button'
          }
        >
          <FiUploadCloud />

          <span>
            {uploading
              ? 'Uploading …'
              : 'Upload'}
          </span>
        </label>

        <input
          id={inputId}
          className="artist-file-input"
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif"
          multiple
          disabled={uploading}
          onChange={(event) => {
            onUpload(
              kind,
              event.target.files
            );

            event.target.value = '';
          }}
        />
      </div>

      {loading ? (
        <div className="artist-artwork-empty">
          Loading images …
        </div>
      ) : images.length === 0 ? (
        <div className="artist-artwork-empty">
          No images uploaded yet.
        </div>
      ) : (
        <div className="artist-artwork-gallery">
          {images.map(
            (image, index) => (
              <div
                className="artist-artwork-item"
                key={image.id}
              >
                <button
                  type="button"
                  className="artist-artwork-image"
                  onClick={() =>
                    onOpen(
                      images,
                      index
                    )
                  }
                  aria-label={`Open ${title} image ${index + 1}`}
                >
                  <img
                    src={
                      image.thumbnailUrl
                    }
                    alt=""
                    loading="lazy"
                  />
                </button>

                <button
                  type="button"
                  className="artist-artwork-delete"
                  onClick={() =>
                    onDelete(image)
                  }
                  disabled={
                    deletingImageId ===
                    image.id
                  }
                  aria-label="Delete image"
                  title="Delete image"
                >
                  <FiTrash2 />
                </button>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}

function ArtistDashboard() {
  const { user } = useAuth();

  // Presence is sent only while this dashboard is visible and the artist is active.
  useEffect(() => {
    if (user?.role !== 'artist') return undefined;
    let active = true;
    let lastActivity = Date.now();
    const markActive = () => { lastActivity = Date.now(); };
    const heartbeat = () => {
      if (!active || document.visibilityState !== 'visible' || Date.now() - lastActivity > 120000) return;
      api.post('/api/auth/artist-presence/heartbeat').catch(() => {});
    };
    const onVisible = () => { if (document.visibilityState === 'visible') { markActive(); heartbeat(); } };
    ['pointerdown','keydown','scroll','touchstart'].forEach(event => window.addEventListener(event,markActive,{passive:true}));
    document.addEventListener('visibilitychange',onVisible);
    heartbeat();
    const interval = window.setInterval(heartbeat,30000);
    return () => {
      active = false; window.clearInterval(interval);
      document.removeEventListener('visibilitychange',onVisible);
      ['pointerdown','keydown','scroll','touchstart'].forEach(event => window.removeEventListener(event,markActive));
    };
  }, [user?.role]);


  const [appointments, setAppointments] =
    useState([]);

  const [bookedDays, setBookedDays] =
    useState([]);

  const [
    selectedAppointmentId,
    setSelectedAppointmentId,
  ] = useState(null);

  const [dayOffset, setDayOffset] =
    useState(0);

  const [sidebarMonth, setSidebarMonth] =
    useState(() => moment().startOf('month'));

  const selectedDayRef = useRef(null);
  const appointmentDetailRef = useRef(null);
  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState('');

  const [viewerImages, setViewerImages] =
    useState([]);

  const [currentImage, setCurrentImage] =
    useState(0);
  const [artworkImages, setArtworkImages] =
    useState([]);

  const [artworkLoading, setArtworkLoading] =
    useState(false);

  const [uploadingKind, setUploadingKind] =
    useState('');

  const [deletingImageId, setDeletingImageId] =
    useState(null);
  const selectedDay = useMemo(
    () =>
      moment()
        .add(dayOffset, 'days')
        .format('YYYY-MM-DD'),
    [dayOffset]
  );
  const bookedDayMoments = useMemo(() => {
    return bookedDays
      .map((item) => {
        const rawDay =
          typeof item === 'string'
            ? item
            : item.date || item.day;

        return moment(
          rawDay,
          'YYYY-MM-DD',
          true
        );
      })
      .filter((day) => day.isValid())
      .sort(
        (a, b) =>
          a.valueOf() - b.valueOf()
      );
  }, [bookedDays]);

  const bookedMonths = useMemo(() => {
    const months = [];

    bookedDayMoments.forEach((day) => {
      const monthKey =
        day.format('YYYY-MM');

      if (
        !months.some(
          (month) =>
            month.format('YYYY-MM') ===
            monthKey
        )
      ) {
        months.push(
          day.clone().startOf('month')
        );
      }
    });

    return months.sort(
      (a, b) =>
        a.valueOf() - b.valueOf()
    );
  }, [bookedDayMoments]);

  const previousBookedMonth = useMemo(() => {
    return (
      [...bookedMonths]
        .reverse()
        .find((month) =>
          month.isBefore(
            sidebarMonth,
            'month'
          )
        ) || null
    );
  }, [
    bookedMonths,
    sidebarMonth,
  ]);

  const nextBookedMonth = useMemo(() => {
    return (
      bookedMonths.find((month) =>
        month.isAfter(
          sidebarMonth,
          'month'
        )
      ) || null
    );
  }, [
    bookedMonths,
    sidebarMonth,
  ]);

  const jumpToBookedMonth = (month) => {
    if (!month) return;

    const firstBookedDay =
      bookedDayMoments.find((day) =>
        day.isSame(month, 'month')
      );

    setSidebarMonth(
      month.clone().startOf('month')
    );

    setSelectedAppointmentId(null);

    if (firstBookedDay) {
      setDayOffset(
        firstBookedDay
          .clone()
          .startOf('day')
          .diff(
            moment().startOf('day'),
            'days'
          )
      );
    }
  };

  

  

  

  

  const sidebarDays = useMemo(
    () =>
      bookedDays
        .map((item) =>
          moment(
            typeof item === 'string'
              ? item
              : item.date,
            'YYYY-MM-DD',
            true
          )
        )
        .filter(
          (day) =>
            day.isValid() &&
            day.isSame(sidebarMonth, 'month')
        )
        .sort(
          (dayA, dayB) =>
            dayA.valueOf() - dayB.valueOf()
        ),
    [bookedDays, sidebarMonth]
  );

  useEffect(() => {
    selectedDayRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }, [selectedDay, sidebarMonth]);

  const selectSidebarDay = (day) => {
    setDayOffset(
      day.clone().startOf('day').diff(
        moment().startOf('day'),
        'days'
      )
    );
  };

  const fetchArtistDay = useCallback(
    async ({ silent = false } = {}) => {
      try {
        if (!silent) {
          setLoading(true);
        }

        setError('');

        const response = await api.get(
          '/api/artist/day',
          {
            params: {
              date: selectedDay,
              refresh: Date.now(),
            },
            headers: {
              'Cache-Control': 'no-cache',
              Pragma: 'no-cache',
            },
          }
        );

        const rows = Array.isArray(
          response.data?.appointments
        )
          ? response.data.appointments
          : [];

        const normalizedAppointments = rows
          .map((appointment) => ({
            ...appointment,
            id: Number(appointment.id),

            workflow_status:
              appointment.workflow_status ||
              'waiting',

            workflow_started_at_ms:
              appointment
                .workflow_started_at_ms
                ? Number(
                  appointment
                    .workflow_started_at_ms
                )
                : null,

            workflow_updated_at_ms:
              appointment
                .workflow_updated_at_ms
                ? Number(
                  appointment
                    .workflow_updated_at_ms
                )
                : null,

            images: Array.isArray(
              appointment.images
            )
              ? appointment.images
              : [],
          }))
          .sort(
            (
              appointmentA,
              appointmentB
            ) =>
              String(
                appointmentA.time || ''
              ).localeCompare(
                String(
                  appointmentB.time || ''
                )
              )
          );

        const returnedBookedDays =
          Array.isArray(response.data?.bookedDays)
            ? response.data.bookedDays
            : [];

        setBookedDays(returnedBookedDays);

        setAppointments(
          normalizedAppointments
        );
      } catch (requestError) {
        console.error(
          'Error fetching artist day:',
          requestError
        );

        if (!silent) {
          setError(
            'Your schedule could not be loaded.'
          );
        }
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [selectedDay]
  );

  useEffect(() => {
    let isActive = true;

    const refreshSilently = () => {
      if (!isActive) {
        return;
      }

      fetchArtistDay({
        silent: true,
      });
    };

    // Initiales Laden mit Ladeanzeige
    fetchArtistDay();

    // Solange die Seite sichtbar ist:
    // alle vier Sekunden aktualisieren
    const intervalId =
      window.setInterval(
        refreshSilently,
        4000
      );

    // Wichtig für iPhone Safari
    const handleVisibilityChange = () => {
      if (
        document.visibilityState ===
        'visible'
      ) {
        refreshSilently();
      }
    };

    const handlePageShow = () => {
      refreshSilently();
    };

    const handleFocus = () => {
      refreshSilently();
    };

    const handleOnline = () => {
      refreshSilently();
    };

    document.addEventListener(
      'visibilitychange',
      handleVisibilityChange
    );

    window.addEventListener(
      'pageshow',
      handlePageShow
    );

    window.addEventListener(
      'focus',
      handleFocus
    );

    window.addEventListener(
      'online',
      handleOnline
    );

    return () => {
      isActive = false;

      window.clearInterval(intervalId);

      document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange
      );

      window.removeEventListener(
        'pageshow',
        handlePageShow
      );

      window.removeEventListener(
        'focus',
        handleFocus
      );

      window.removeEventListener(
        'online',
        handleOnline
      );
    };
  }, [fetchArtistDay]);

  const pendingWorkspaceSession = useRef(null);

  const artistAppointments =
    appointments;

  useEffect(() => {
    if (pendingWorkspaceSession.current) {
      const pending = pendingWorkspaceSession.current;
      if (artistAppointments.some(a => a.id === pending.id)) {
        pendingWorkspaceSession.current = null;
        setSelectedAppointmentId(pending.id);
      }
      return;
    }
    if (!artistAppointments.length) {
      setSelectedAppointmentId(null);
      return;
    }

    if (selectedAppointmentId === null) {
      return;
    }

    const selectedStillExists =
      artistAppointments.some(
        (appointment) =>
          appointment.id ===
          selectedAppointmentId
      );

    if (!selectedStillExists) {
      setSelectedAppointmentId(null);
    }
  }, [
    artistAppointments,
    selectedAppointmentId,
  ]);

  const selectedAppointment =
    artistAppointments.find(
      (appointment) =>
        appointment.id ===
        selectedAppointmentId
    ) || null;

  useEffect(() => {
    if (!selectedAppointmentId) {
      return;
    }

    window.setTimeout(() => {
      appointmentDetailRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 100);
  }, [selectedAppointmentId]);

  const fetchArtworkImages =
    useCallback(
      async (appointmentId) => {
        if (!appointmentId) {
          setArtworkImages([]);
          return;
        }

        try {
          setArtworkLoading(true);
          setError('');

          const response = await api.get(
            `/api/artist/appointments/${appointmentId}/artwork-images`
          );

          const images = Array.isArray(
            response.data?.images
          )
            ? response.data.images
            : [];

          setArtworkImages(images);
        } catch (requestError) {
          console.error(
            'Artwork images could not be loaded:',
            requestError
          );

          setArtworkImages([]);

          setError(
            requestError.response?.data?.message ||
            'The stencil and result images could not be loaded.'
          );
        } finally {
          setArtworkLoading(false);
        }
      },
      []
    );

  useEffect(() => {
    if (!selectedAppointment?.id) {
      setArtworkImages([]);
      return;
    }

    fetchArtworkImages(
      selectedAppointment.id
    );
  }, [
    selectedAppointment?.id,
    fetchArtworkImages,
  ]);

  const stencilImages = useMemo(
    () =>
      artworkImages.filter(
        (image) =>
          image.kind === 'stencil'
      ),
    [artworkImages]
  );

  const resultImages = useMemo(
    () =>
      artworkImages.filter(
        (image) =>
          image.kind === 'result'
      ),
    [artworkImages]
  );

  const selectedAppointmentImages =
    useMemo(
      () =>
        getAppointmentImages(
          selectedAppointment
        ),
      [selectedAppointment]
    );

  const selectedFullImageUrls =
    useMemo(
      () =>
        selectedAppointmentImages.map(
          (image) => image.fullUrl
        ),
      [selectedAppointmentImages]
    );

  const nextAppointment = useMemo(() => {
    const inProgress =
      artistAppointments.find(
        (appointment) =>
          appointment.workflow_status ===
          'in_progress'
      );

    if (inProgress) {
      return inProgress;
    }

    const now = moment();

    return (
      artistAppointments.find(
        (appointment) => {
          if (
            appointment.workflow_status ===
            'done'
          ) {
            return false;
          }

          const appointmentTime = moment(
            `${selectedDay} ${appointment.time}`,
            [
              'YYYY-MM-DD HH:mm:ss',
              'YYYY-MM-DD HH:mm',
            ],
            true
          );

          return (
            appointmentTime.isValid() &&
            appointmentTime.isSameOrAfter(
              now
            )
          );
        }
      ) ||
      artistAppointments.find(
        (appointment) =>
          appointment.workflow_status !==
          'done'
      )
    );
  }, [
    artistAppointments,
    selectedDay,
  ]);

  const totalMinutes =
    artistAppointments.reduce(
      (sum, appointment) =>
        sum +
        Number(
          appointment.duration || 0
        ),
      0
    );

  const updateWorkflow = async (
    appointmentId
  ) => {
    try {
      setError('');

      const response = await api.put(
        `/api/artist/appointments/${appointmentId}/workflow-status`
      );

      setAppointments((previous) =>
        previous.map((appointment) =>
          appointment.id === appointmentId
            ? {
              ...appointment,
              workflow_status:
                response.data.workflow_status,

              workflow_started_at_ms:
                response.data
                  .workflow_started_at_ms
                  ? Number(
                    response.data
                      .workflow_started_at_ms
                  )
                  : null,

              workflow_updated_at_ms:
                response.data
                  .workflow_updated_at_ms
                  ? Number(
                    response.data
                      .workflow_updated_at_ms
                  )
                  : null,
            }
            : appointment
        )
      );
    } catch (requestError) {
      console.error(
        'Error updating workflow:',
        requestError
      );

      setError(
        'The status could not be updated.'
      );
    }
  };

  const uploadArtworkImages = async (
    kind,
    fileList
  ) => {
    const files = Array.from(
      fileList || []
    );

    if (
      !selectedAppointment?.id ||
      files.length === 0
    ) {
      return;
    }

    if (files.length > 8) {
      setError(
        'Please upload a maximum of 8 images at once.'
      );
      return;
    }

    const formData = new FormData();

    formData.append('kind', kind);

    files.forEach((file) => {
      formData.append(
        'images',
        file
      );
    });

    try {
      setError('');
      setUploadingKind(kind);

      const response = await api.post(
        `/api/artist/appointments/${selectedAppointment.id}/artwork-images`,
        formData
      );

      const uploadedImages =
        Array.isArray(
          response.data?.images
        )
          ? response.data.images
          : [];

      setArtworkImages(
        (previous) => [
          ...previous,
          ...uploadedImages,
        ]
      );
    } catch (requestError) {
      console.error(
        'Artwork upload failed:',
        requestError
      );

      setError(
        requestError.response?.data?.message ||
        'The images could not be uploaded.'
      );
    } finally {
      setUploadingKind('');
    }
  };

  const deleteArtworkImage = async (
    image
  ) => {
    if (!image?.id) {
      return;
    }

    const confirmed =
      window.confirm(
        'Delete this image permanently?'
      );

    if (!confirmed) {
      return;
    }

    try {
      setError('');
      setDeletingImageId(image.id);

      await api.delete(
        `/api/artist/appointment-artwork-images/${image.id}`
      );

      setArtworkImages(
        (previous) =>
          previous.filter(
            (item) =>
              item.id !== image.id
          )
      );
    } catch (requestError) {
      console.error(
        'Artwork deletion failed:',
        requestError
      );

      setError(
        requestError.response?.data?.message ||
        'The image could not be deleted.'
      );
    } finally {
      setDeletingImageId(null);
    }
  };

  const openArtworkImages = (
    images,
    imageIndex
  ) => {
    setViewerImages(
      images.map(
        (image) => image.fullUrl
      )
    );

    setCurrentImage(imageIndex);
  };

  const toggleAppointment = (
    appointmentId
  ) => {
    setSelectedAppointmentId(
      (currentId) =>
        currentId === appointmentId
          ? null
          : appointmentId
    );
  };

  const openAppointment = (
    appointmentId
  ) => {
    setSelectedAppointmentId(
      appointmentId
    );
  };

  const closeImageViewer = () => {
    setViewerImages([]);
    setCurrentImage(0);
  };

  const phone = cleanPhone(
    selectedAppointment?.phone
  );

  const greeting = useMemo(() => {
    const hour = moment().hour();

    if (hour < 12) {
      return 'Good morning';
    }

    if (hour < 18) {
      return 'Good afternoon';
    }

    return 'Good evening';
  }, []);

  const [workspaceTab, setWorkspaceTab] = useState('day');
  const openWorkspaceSession = (session) => {
    if (session) {
      pendingWorkspaceSession.current = { id: Number(session.id) };
      setDayOffset(moment(session.day).startOf('day').diff(moment().startOf('day'), 'days'));
      setSelectedAppointmentId(Number(session.id));
    }
    setWorkspaceTab('day');
  };
  return (
    <main className="artist-page">
      <header className="artist-header">
        <div>
          <span className="artist-eyebrow">
            Prime Ink Artist
          </span>

          <h1>
            {greeting}, {user?.name || 'Artist'}
          </h1>
          <p>
            {moment(selectedDay).format(
              'dddd, D MMMM YYYY'
            )}
          </p>
        </div>

      </header>

      <div className="artist-workspace-tabs" role="tablist" aria-label="Artist workspace">
        <button role="tab" aria-selected={workspaceTab === 'day'} onClick={() => setWorkspaceTab('day')}>My Day</button>
        <button role="tab" aria-selected={workspaceTab === 'work'} onClick={() => setWorkspaceTab('work')}>My Work</button>
      </div>
      {workspaceTab === 'work' && <ArtistMyWork onSession={openWorkspaceSession} />}
      {workspaceTab === 'day' && <>
      <aside
        className="artist-day-sidebar"
        aria-label="Choose a day"
      >

        <div className="artist-day-sidebar-header">
          <button
            type="button"
            onClick={() =>
              jumpToBookedMonth(
                previousBookedMonth
              )
            }
            disabled={
              !previousBookedMonth
            }
            aria-label="Previous booked month"
            title={
              previousBookedMonth
                ? `Previous: ${previousBookedMonth.format(
                  'MMMM YYYY'
                )}`
                : 'No previous bookings'
            }
          >
            <FiChevronLeft />
          </button>

          <strong>
            {sidebarMonth.format(
              'MMMM YYYY'
            )}
          </strong>

          <button
            type="button"
            onClick={() =>
              jumpToBookedMonth(
                nextBookedMonth
              )
            }
            disabled={
              !nextBookedMonth
            }
            aria-label="Next booked month"
            title={
              nextBookedMonth
                ? `Next: ${nextBookedMonth.format(
                  'MMMM YYYY'
                )}`
                : 'No further bookings'
            }
          >
            <FiChevronRight />
          </button>
        </div>

        <div className="artist-day-list">
          {sidebarDays.length === 0 && (
            <div className="artist-day-empty">
              No bookings
            </div>
          )}

          {sidebarDays.map((day) => {
            const date = day.format('YYYY-MM-DD');
            const isToday = day.isSame(moment(), 'day');
            const isSelected = date === selectedDay;

            return (
              <button
                key={date}
                ref={isSelected ? selectedDayRef : null}
                type="button"
                className={`artist-day-button${isToday ? ' is-today' : ''
                  }${isSelected ? ' is-selected' : ''
                  }`}
                onClick={() => selectSidebarDay(day)}
                aria-current={isToday ? 'date' : undefined}
                aria-pressed={isSelected}
              >
                <span>{day.format('dd')}</span>
                <strong>{day.format('D')}</strong>
              </button>
            );
          })}
        </div>
      </aside>

      {error && (
        <div className="artist-error">
          {error}
        </div>
      )}

      <section className="artist-layout">
        <div className="artist-left-column">
          <section className="artist-panel artist-schedule-panel">
            <div className="artist-panel-heading">
              <div>
                <h2>My Day</h2>

                <span>
                  {artistAppointments.length}{' '}
                  {artistAppointments.length === 1
                    ? 'appointment'
                    : 'appointments'}
                </span>
              </div>

              <FiCalendar />
            </div>

            {loading ? (
              <div className="artist-empty">
                Loading your schedule …
              </div>
            ) : artistAppointments.length ===
              0 ? (
              <div className="artist-empty">
                No appointments for this
                day.
              </div>
            ) : (
              <div className="artist-appointment-list">
                {artistAppointments.map(
                  (appointment) => {
                    const isSelected =
                      appointment.id ===
                      selectedAppointmentId;

                    return (
                      <button
                        key={appointment.id}
                        type="button"
                        className={`
                          artist-appointment-row
                          ${isSelected
                            ? 'selected'
                            : ''
                          }
                          ${appointment.workflow_status
                          }
                        `}
                        onClick={() =>
                          toggleAppointment(
                            appointment.id
                          )
                        }
                        aria-expanded={
                          isSelected
                        }
                      >
                        <span className="artist-status-dot" />

                        <strong>
                          {formatTime(
                            appointment.time
                          )}
                        </strong>

                        <span className="artist-row-client">
                          <b>
                            {
                              appointment.clientName
                            }
                          </b>

                          <small>
                            {appointment.project_id && <span>{appointment.project_title} · Session {appointment.project_session_number}/{appointment.project_session_count}<br /></span>}
                            {appointment.description ||
                              appointmentType(
                                appointment
                              )}
                          </small>
                        </span>

                        <span className="artist-row-status">
                          {
                            STATUS_LABELS[
                            appointment
                              .workflow_status
                            ]
                          }
                        </span>

                        <FiChevronRight />
                      </button>
                    );
                  }
                )}
              </div>
            )}
          </section>

          <section className="artist-panel artist-queue-panel">
            <div className="artist-panel-heading">
              <div>
                <h2>Live Queue</h2>
                <span>Your schedule</span>
              </div>

              <FiZap />
            </div>

            {artistAppointments.length ===
              0 ? (
              <div className="artist-empty">
                No appointments in the
                queue.
              </div>
            ) : (
              artistAppointments.map(
                (appointment, index) => (
                  <button
                    type="button"
                    key={appointment.id}
                    className={`
                      artist-queue-row
                      ${nextAppointment?.id ===
                        appointment.id
                        ? 'current'
                        : ''
                      }
                    `}
                    onClick={() =>
                      openAppointment(
                        appointment.id
                      )
                    }
                  >
                    <span className="artist-queue-number">
                      {index + 1}
                    </span>

                    <span>
                      <small>
                        {nextAppointment?.id ===
                          appointment.id
                          ? 'NOW / NEXT'
                          : 'UP NEXT'}
                      </small>

                      <strong>
                        {formatTime(
                          appointment.time
                        )}
                      </strong>
                    </span>

                    <span className="artist-queue-client">
                      <b>
                        {
                          appointment.clientName
                        }
                      </b>

                      <small>
                        {appointmentType(
                          appointment
                        )}
                      </small>
                    </span>

                    <FiChevronRight />
                  </button>
                )
              )
            )}
          </section>
        </div>

        <aside
          ref={appointmentDetailRef}
          className={`
    artist-panel
    artist-detail-panel
    ${selectedAppointment
              ? 'is-open'
              : 'is-empty'
            }
  `}
        >
          {!selectedAppointment ? (
            <div className="artist-empty">
              Select an appointment.
            </div>
          ) : (
            <>
              <div className="artist-detail-top">
                <span className="artist-detail-time">
                  {formatTime(
                    selectedAppointment.time
                  )}
                </span>

                <span
                  className={`
                    artist-status-pill
                    ${selectedAppointment
                      .workflow_status
                    }
                  `}
                >
                  {
                    STATUS_LABELS[
                    selectedAppointment
                      .workflow_status
                    ]
                  }
                </span>
              </div>

              <h2>
                {
                  selectedAppointment.clientName
                }
              </h2>

              <p className="artist-detail-type">
                {appointmentType(
                  selectedAppointment
                )}
              </p>

              <div className="artist-contact-actions">
                {selectedAppointment.phone && (
                  <a
                    href={`tel:${selectedAppointment.phone}`}
                  >
                    <FiPhone />
                    <span>Call</span>
                  </a>
                )}

                {phone.length >= 11 && (
                  <a
                    href={`https://wa.me/${phone}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <FiMessageCircle />
                    <span>WhatsApp</span>
                  </a>
                )}
              </div>

              {selectedAppointment.project_id && <ArtistProjectJourney appointment={selectedAppointment} onSession={openWorkspaceSession} />}
              <div className="artist-detail-section">
                <span>Project</span>

                <p>
                  {selectedAppointment.project_id && <span>{selectedAppointment.project_title} · Session {selectedAppointment.project_session_number}/{selectedAppointment.project_session_count}<br /></span>}
                  {selectedAppointment.description ||
                    'No project description available.'}
                  {selectedAppointment.project_session_note && <span><br />Session note: {selectedAppointment.project_session_note}</span>}

                </p>
              </div>

              {selectedAppointmentImages.length >
                0 && (
                  <div className="artist-detail-section">
                    <div className="artist-images-heading">
                      <span>
                        Project images
                      </span>

                      <strong>
                        {
                          selectedAppointmentImages.length
                        }
                      </strong>
                    </div>

                    <div className="artist-image-gallery">
                      {selectedAppointmentImages.map(
                        (image, index) => (
                          <button
                            key={image.id}
                            type="button"
                            className={
                              index === 0
                                ? 'artist-image-thumb is-cover'
                                : 'artist-image-thumb'
                            }
                            onClick={() => {
                              setViewerImages(
                                selectedFullImageUrls
                              );

                              setCurrentImage(
                                index
                              );
                            }}
                            aria-label={`Open project image ${index + 1
                              }`}
                          >
                            <img
                              src={
                                image.thumbnailUrl
                              }
                              alt=""
                              loading="lazy"
                            />

                            {index === 0 &&
                              image.isProjectImage && (
                                <span>
                                  Cover
                                </span>
                              )}
                          </button>
                        )
                      )}
                    </div>
                  </div>
                )}

              <div className="artist-detail-section artist-artwork-section">
                <ArtworkUploadSection
                  kind="stencil"
                  title="Stencil"
                  hint="Design prepared for the appointment"
                  images={stencilImages}
                  loading={artworkLoading}
                  uploading={
                    uploadingKind ===
                    'stencil'
                  }
                  deletingImageId={
                    deletingImageId
                  }
                  onUpload={
                    uploadArtworkImages
                  }
                  onDelete={
                    deleteArtworkImage
                  }
                  onOpen={
                    openArtworkImages
                  }
                />

                <ArtworkUploadSection
                  kind="result"
                  title="Result"
                  hint="Your finished piece — automatically added to My Work"
                  images={resultImages}
                  loading={artworkLoading}
                  uploading={
                    uploadingKind ===
                    'result'
                  }
                  deletingImageId={
                    deletingImageId
                  }
                  onUpload={
                    uploadArtworkImages
                  }
                  onDelete={
                    deleteArtworkImage
                  }
                  onOpen={
                    openArtworkImages
                  }
                />
              </div>

              <div className="artist-detail-grid">
                <div>
                  <span>Duration</span>

                  <strong>
                    {selectedAppointment.duration
                      ? `${selectedAppointment.duration} min`
                      : '–'}
                  </strong>
                </div>

                <div>
                  <span>Day booked</span>

                  <strong>
                    {totalMinutes
                      ? `${Math.round(
                        (totalMinutes /
                          60) *
                        10
                      ) / 10
                      } hrs`
                      : '–'}
                  </strong>
                </div>
              </div>

              <div className="artist-detail-section">
                <span>Workflow</span>

                <div className="artist-workflow">
                  {STATUS_ORDER.map(
                    (status) => (
                      <div
                        key={status}
                        className={
                          selectedAppointment.workflow_status ===
                            status
                            ? 'active'
                            : ''
                        }
                      >
                        {status ===
                          'waiting' && (
                            <FiClock />
                          )}

                        {status ===
                          'arrived' && (
                            <FiUser />
                          )}

                        {status ===
                          'in_progress' && (
                            <FiZap />
                          )}

                        {status ===
                          'done' && (
                            <FiCheckCircle />
                          )}

                        <small>
                          {
                            STATUS_LABELS[
                            status
                            ]
                          }
                        </small>
                      </div>
                    )
                  )}
                </div>
              </div>

              <button
                type="button"
                className={`
                  artist-workflow-button
                  ${selectedAppointment
                    .workflow_status
                  }
                `}
                onClick={() =>
                  updateWorkflow(
                    selectedAppointment.id
                  )
                }
              >
                {selectedAppointment.workflow_status ===
                  'in_progress' ? (
                  <>
                    <FiCheckCircle />
                    Finish session
                  </>
                ) : selectedAppointment.workflow_status ===
                  'done' ? (
                  <>
                    <FiRefreshCw />
                    Reset to waiting
                  </>
                ) : (
                  <>
                    <FiZap />
                    Next status
                  </>
                )}
              </button>
            </>
          )}
        </aside>
      </section>

      </>}
      <AppointmentImageViewer
        images={viewerImages}
        currentImage={currentImage}
        setCurrentImage={setCurrentImage}
        onClose={closeImageViewer}
        onShare={(url) => {
          window.open(
            url,
            '_blank',
            'noopener,noreferrer'
          );
        }}
      />
    </main>
  );
}

export default ArtistDashboard;
